import type { ShapeData } from '@/lib/shapes/types/shape-data'

const GROUP_ISOLATION_DIM_OPACITY_START = 0.45
const GROUP_ISOLATION_DIM_OPACITY_STEP = 0.05
const GROUP_ISOLATION_DIM_OPACITY_MIN = 0.1

/** Opacity multiplier for shapes/text outside the active group-isolation subtree. */
export function getGroupIsolationOpacityForOutsideLayer(layerDistance: number): number {
  const layer = Math.max(1, layerDistance)
  const raw = GROUP_ISOLATION_DIM_OPACITY_START - (layer - 1) * GROUP_ISOLATION_DIM_OPACITY_STEP

  return Math.max(GROUP_ISOLATION_DIM_OPACITY_MIN, raw)
}

export function buildActiveIsolationAncestorDepthMap(
  activeGroupIsolationId: string,
  shapesById: Map<string, ShapeData>
): Map<string, number> {
  const map = new Map<string, number>()
  let cursor: string | undefined = activeGroupIsolationId
  let depth = 0

  while (cursor) {
    map.set(cursor, depth)
    cursor = shapesById.get(cursor)?.parentGroupId
    depth += 1
  }

  return map
}

/**
 * Same dimming rule as `ShapeManager`: objects inside the active isolation subtree stay at full
 * opacity; others fade based on how far their enclosing group sits from the isolation anchor.
 */
export function computeIsolationOpacityMultiplierForOutsiders(args: {
  activeGroupIsolationId: string | null | undefined
  shapesById: Map<string, ShapeData>
  ownParentGroupId: string | undefined
  isInsideActiveIsolationSubtree: boolean
  activeIsolationAncestorDepthById?: Map<string, number>
}): number {
  const {
    activeGroupIsolationId,
    shapesById,
    ownParentGroupId,
    isInsideActiveIsolationSubtree,
    activeIsolationAncestorDepthById: depthMapIn,
  } = args

  if (!activeGroupIsolationId) return 1
  if (isInsideActiveIsolationSubtree) return 1

  const activeIsolationAncestorDepthById =
    depthMapIn ?? buildActiveIsolationAncestorDepthMap(activeGroupIsolationId, shapesById)

  let outsideLayerDistance = 3

  if (
    ownParentGroupId === undefined &&
    shapesById.get(activeGroupIsolationId)?.parentGroupId === undefined
  ) {
    outsideLayerDistance = 1
  } else {
    let cursor = ownParentGroupId

    while (cursor) {
      const activeDepth = activeIsolationAncestorDepthById.get(cursor)

      if (activeDepth !== undefined) {
        outsideLayerDistance = Math.max(1, activeDepth)
        break
      }

      cursor = shapesById.get(cursor)?.parentGroupId
    }
  }

  return getGroupIsolationOpacityForOutsideLayer(outsideLayerDistance)
}
